import { CustomFieldDto } from 'types/customFields';
import { Office, OfficeCreateDto, OfficeForm } from 'types/offices';

const templatesOfType = (office: Office, type: 'INVOICE' | 'INVOICE_SPEC') =>
    office.documentTemplates?.filter((template) => type === template.documentTemplateType) ?? [];

/** The edit form's values for a stored office, its custom fields' values as `useCustomFields` converts them. */
export const officeDtoToForm = (office: Office, customFieldValues: Record<string, unknown>): OfficeForm => ({
    name: office.name ?? '',
    company: office.company?.id ? { id: office.company.id, name: office.company.name } : null,
    companies: office.companies?.map((val) => ({ id: val.id, name: val.name })) ?? [],
    emailAddress: office.emailAddress ?? '',
    invoicingEmailAddress: office.invoicingEmailAddress ?? '',
    deadlineEventEmailAddress: office.deadlineEventEmailAddress ?? '',
    phoneNumber: office.phoneNumber ?? '',
    abbreviation: office.abbreviation ?? '',
    invoiceAbbreviation: office.invoiceAbbreviation ?? '',
    address: office.address ?? null,
    localAddress: office.localAddress ?? null,
    isActive: office.isActive ?? true,
    invoiceSpecTemplates: templatesOfType(office, 'INVOICE_SPEC'),
    invoiceTemplates: templatesOfType(office, 'INVOICE'),
    defaultLocalCountryInvoiceTemplate:
        templatesOfType(office, 'INVOICE').find((template) => template.isDefault) || null,
    defaultForeignCountryInvoiceTemplate:
        templatesOfType(office, 'INVOICE').find((template) => template.isDefaultForeign) || null,
    defaultLocalCountryInvoiceSpecTemplate:
        templatesOfType(office, 'INVOICE_SPEC').find((template) => template.isDefault) || null,
    defaultForeignCountryInvoiceSpecTemplate:
        templatesOfType(office, 'INVOICE_SPEC').find((template) => template.isDefaultForeign) || null,
    ...customFieldValues,
});

export const officeFormToRequest = (form: OfficeForm, customFields: CustomFieldDto[]): OfficeCreateDto => ({
    name: form.name,
    invoiceAbbreviation: form.invoiceAbbreviation,
    companyId: form.company?.id ?? null,
    companies: form.companies?.map((company) => company.id) ?? [],
    emailAddress: form.emailAddress || null,
    invoicingEmailAddress: form.invoicingEmailAddress || null,
    deadlineEventEmailAddress: form.deadlineEventEmailAddress || null,
    phoneNumber: form.phoneNumber || null,
    abbreviation: form.abbreviation,
    isActive: form.isActive,
    address: form.address,
    localAddress: form.localAddress,
    documentTemplateIds: [
        ...(form.invoiceSpecTemplates?.map((template) => template.id) || []),
        ...(form.invoiceTemplates?.map((template) => template.id) || []),
    ],
    defaultLocalCountryInvoiceTemplate: form.defaultLocalCountryInvoiceTemplate?.id,
    defaultForeignCountryInvoiceTemplate: form.defaultForeignCountryInvoiceTemplate?.id,
    defaultLocalCountryInvoiceSpecTemplate: form.defaultLocalCountryInvoiceSpecTemplate?.id,
    defaultForeignCountryInvoiceSpecTemplate: form.defaultForeignCountryInvoiceSpecTemplate?.id,
    ...(customFields.length > 0 ? { customFields: customFields } : {}),
});

/** The form fields each card of the office page edits; a template list and its defaults stay together. */
export const OFFICE_SECTION_FIELDS = {
    office: ['company', 'companies', 'name', 'abbreviation', 'isActive'],
    contact: ['emailAddress', 'phoneNumber', 'address', 'localAddress'],
    invoicing: [
        'invoiceAbbreviation',
        'invoicingEmailAddress',
        'deadlineEventEmailAddress',
        'invoiceTemplates',
        'defaultLocalCountryInvoiceTemplate',
        'defaultForeignCountryInvoiceTemplate',
        'invoiceSpecTemplates',
        'defaultLocalCountryInvoiceSpecTemplate',
        'defaultForeignCountryInvoiceSpecTemplate',
    ],
    'custom-fields': [],
} satisfies Record<string, (keyof OfficeForm)[]>;

export type OfficeSectionId = keyof typeof OFFICE_SECTION_FIELDS;
